import { logger } from '@vagabondinternet/logging';
import { AUTH_PROVIDERS, type AuthProvider, isEnabledProvider, type SocialProvider } from '@words-happen/ui-components';
import { useCallback, useEffect, useRef, useState } from 'react';
import { authErrorCode, authFailure, type SocialSignIn, type SocialSignInResult, sharedAuthMessage } from '../api';

/** "We couldn't sign you in with Google. Try again.": a provider's failure with no message of its own. */
export const socialSignInFailed = (provider?: SocialProvider): string =>
  `We couldn't sign you in${provider ? ` with ${AUTH_PROVIDERS[provider].name}` : ''}. Try again.`;

/**
 * The alert for a failed social sign-in, or undefined for none: backing out of the provider's page is never an
 * error. The sign-up allowlist's refusal is the page's own (`notAllowed`) and never names the address. An address that
 * already has an account created another way is told to sign in as before, never which way.
 */
export const socialSignInErrorMessage = (
  error: unknown,
  notAllowed: string,
  provider?: SocialProvider,
): string | undefined => {
  const failure = authFailure(error);
  if (failure === 'cancelled') {
    return undefined;
  }
  if (failure === 'not-allowed') {
    return notAllowed;
  }
  return sharedAuthMessage(failure) ?? socialSignInFailed(provider);
};

export interface SocialSignInOptions {
  socialSignIn: SocialSignIn;
  /** The providers that may be started; any other is ignored. */
  enabled: readonly SocialProvider[];
  /** The page's message for the sign-up allowlist's refusal. */
  notAllowedMessage: string;
  /** Where the verification email sent to an unverified player continues to: /verify-email, keeping return_to. */
  continueUrl: string;
  /**
   * Called once with a completed redirect's player, to hand them off or, unverified, send them to /verify-email (the
   * verification email has been sent, unless `verificationSent` is false).
   */
  onSignedIn: (result: SocialSignInResult) => void | Promise<void>;
}

export interface SocialSignInState {
  /**
   * Whether this page load is completing a redirect, decided once on mount: the page shows "Signing you in…", and
   * silent sign-in must not run as well, so the handoff happens exactly once.
   */
  redirectPending: boolean;
  /** Completing the redirect is still in progress. */
  finishing: boolean;
  /** The provider whose redirect is starting, for its button's spinner. */
  busyProvider?: SocialProvider;
  /** Why the last social sign-in failed. */
  error?: string;
  /** Starts `provider`'s redirect when it is enabled; ignores email and any other. */
  select: (provider: AuthProvider) => void;
  clearError: () => void;
}

/**
 * Social sign-in on /sign-in and /register: a provider's button leaves for its sign-in page (signInWithRedirect), and
 * when the player comes back to the same page, the redirect is completed (getRedirectResult) before anything else,
 * sending an unverified player the verification email once per sign-in.
 * A player who backs out of the provider's page sees the form again with no error. Logs hold only error codes.
 */
export const useSocialSignIn = ({
  socialSignIn,
  enabled,
  notAllowedMessage,
  continueUrl,
  onSignedIn,
}: SocialSignInOptions): SocialSignInState => {
  const [redirectPending] = useState(() => socialSignIn.redirectPending());
  const [finishing, setFinishing] = useState(redirectPending);
  const [busyProvider, setBusyProvider] = useState<SocialProvider>();
  const [error, setError] = useState<string>();
  // React StrictMode mounts twice in development; a redirect result can only be had once.
  const finishStarted = useRef(false);
  // The handoff's callbacks change between renders; completing the redirect uses the latest.
  const signedIn = useRef(onSignedIn);
  signedIn.current = onSignedIn;

  useEffect(() => {
    if (!redirectPending || finishStarted.current) {
      return;
    }
    finishStarted.current = true;
    socialSignIn
      .finish({ continueUrl })
      .then(async (result) => {
        if (result) {
          await signedIn.current(result);
        }
      })
      .catch((cause) => {
        logger.error({ message: 'Completing a social sign-in failed', code: authErrorCode(cause) });
        setError(socialSignInErrorMessage(cause, notAllowedMessage));
      })
      .finally(() => setFinishing(false));
  }, [redirectPending, socialSignIn, notAllowedMessage, continueUrl]);

  // Back from the provider's page into a page restored from the back-forward cache: its buttons work again.
  useEffect(() => {
    const restored = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setBusyProvider(undefined);
      }
    };
    window.addEventListener('pageshow', restored);
    return () => window.removeEventListener('pageshow', restored);
  }, []);

  const select = useCallback(
    (provider: AuthProvider) => {
      if (!isEnabledProvider(enabled, provider) || busyProvider) {
        return;
      }
      setError(undefined);
      setBusyProvider(provider);
      // On success the page is leaving, so the button keeps its spinner.
      socialSignIn.start(provider).catch((cause) => {
        logger.error({ message: 'Starting a social sign-in failed', code: authErrorCode(cause), provider });
        setError(socialSignInErrorMessage(cause, notAllowedMessage, provider));
        setBusyProvider(undefined);
      });
    },
    [enabled, busyProvider, socialSignIn, notAllowedMessage],
  );

  const clearError = useCallback(() => setError(undefined), []);

  return { redirectPending, finishing, busyProvider, error, select, clearError };
};
